/**
 * @fileoverview The shipment lifecycle (`shipments.status`) — its vocabulary, its status machine
 * and its badge tones, declared once (LEAVE-CANON-001 R14 F-R14-7c; owner ruling R-71).
 *
 * ## Why it lives here
 *
 * Until this module the vocabulary had THREE spellings: pos-api's `SHIPMENT_STATUS`
 * (`src/constants/statuses.js`), pos-web's hand mirror (`SHIPMENT_STATUSES`, "change one, change
 * the other") and a third list inside pos-web's validators — and the one time the API grew a value
 * (`in_transit`) the mirrors went a year without it. The machine lived in pos-api's model alone, so
 * a screen could not ask what is possible next. Both halves are here now, and pos-api (constants,
 * model, validators, the CHECK migration) and pos-web (the Shipments tab, its status action, the
 * validators) import them.
 *
 * ## `awaiting_pickup` (R-71, verbatim)
 *
 * "an `awaiting_pickup` shipment status between `processing` and `shipped` (label printed, parcel
 * staged), set by the label / manifest step, with migration + CHECK + Joi + UI vocabulary; the
 * Shipments tile counts it."
 *
 * ## The machine, and the two decisions R-71 left open
 *
 * - `processing → awaiting_pickup → shipped` is the staged path R-71 names.
 * - `processing → shipped` STAYS: a parcel handed straight to a carrier at the counter (or walked
 *   to the post office) never sits on a pickup shelf, and the PostalMate import already records
 *   that hand-over as `shipped` from a packed row (`ShipmentTransformer.transformStatus`:
 *   `TENDEREDSTATUS` → `shipped`). Forcing a staged step that never happened would record a lie.
 * - `awaiting_pickup → cancelled` is LEGAL: a staged parcel is still in the store and its label
 *   can still be voided — every state before the carrier's first scan (`pending`, `processing`,
 *   `shipped`) was already cancellable, and a state in the middle of that run that could not be
 *   cancelled would be the one exception with no reason.
 *
 * ## `imported_unknown` (R-74, Q-R14-21 — "imported, final state unknown")
 *
 * A PostalMate shipment that was never packed, tendered or voided carries no evidence of what
 * became of it: PostalMate records nothing after entry, and the import cannot see the carrier. Until
 * R-74 those rows read `pending` — 152,293 of them in `cmp_1c67dc8bdc1f`, entered 2004 → 2026 and
 * never closed, because nothing on this side can advance a shipment nobody is holding. The owner
 * ruled a TERMINAL value that says exactly that:
 *
 * - the name is the ruling's own words, and fits `shipments.status` `varchar(20)` (16 characters);
 * - it is written ONLY by the import (`ShipmentTransformer.transformStatus`) and by the one close-out
 *   migration (`20260929100000_leave_canon_001_close_imported_pending_shipments`): no status leads
 *   to it and it leads nowhere, so no screen or route can set it or move a row out of it;
 * - its badge is `muted` — the record is closed, but its outcome is neither the happy end
 *   (`success`) nor an abandonment (`danger`), and claiming either would be a guess.
 *
 * `SHIPMENT_STATUS_ASSIGNABLE_VALUES` is the set a CALLER may request: every status some
 * transition reaches. It excludes `pending` (the creation default, which nothing moves back to) and
 * `imported_unknown` — what a status-update validator admits, while a filter reads every value.
 *
 * Every status is a KEY, a terminal one mapping to `[]` (`delivered`, `cancelled`) — the
 * `PAYROLL_RUN_STATUS_TRANSITIONS` convention, so a reader can list a status's targets without
 * knowing which convention was used (`@puffin-core/model-base`'s `assertTransition` reads both).
 *
 * @module @puffin-core/status-taxonomy/shipmentStatus
 */

'use strict';

const { BADGE_TONE } = require('./badgeTones');

/**
 * The shipment lifecycle, in lifecycle order.
 *
 * @type {Readonly<Object<string, string>>}
 */
const SHIPMENT_STATUS = Object.freeze({
  PENDING: 'pending',
  PROCESSING: 'processing',
  AWAITING_PICKUP: 'awaiting_pickup',
  SHIPPED: 'shipped',
  IN_TRANSIT: 'in_transit',
  DELIVERED: 'delivered',
  RETURNED: 'returned',
  CANCELLED: 'cancelled',
  // Import-only and terminal (R-74) — listed last because it sits outside the lifecycle.
  IMPORTED_UNKNOWN: 'imported_unknown',
});

/**
 * Every status, in lifecycle order — the order the database CHECK and the status filter read.
 *
 * @type {ReadonlyArray<string>}
 */
const SHIPMENT_STATUS_VALUES = Object.freeze(Object.values(SHIPMENT_STATUS));

/**
 * Every legal transition, spelled once. See the module header for the reasoning.
 *
 * @type {Readonly<Object<string, ReadonlyArray<string>>>}
 */
const SHIPMENT_STATUS_TRANSITIONS = Object.freeze({
  [SHIPMENT_STATUS.PENDING]: Object.freeze([SHIPMENT_STATUS.PROCESSING, SHIPMENT_STATUS.CANCELLED]),
  [SHIPMENT_STATUS.PROCESSING]: Object.freeze([
    SHIPMENT_STATUS.AWAITING_PICKUP,
    SHIPMENT_STATUS.SHIPPED,
    SHIPMENT_STATUS.CANCELLED,
  ]),
  [SHIPMENT_STATUS.AWAITING_PICKUP]: Object.freeze([
    SHIPMENT_STATUS.SHIPPED,
    SHIPMENT_STATUS.CANCELLED,
  ]),
  [SHIPMENT_STATUS.SHIPPED]: Object.freeze([SHIPMENT_STATUS.IN_TRANSIT, SHIPMENT_STATUS.CANCELLED]),
  [SHIPMENT_STATUS.IN_TRANSIT]: Object.freeze([
    SHIPMENT_STATUS.DELIVERED,
    SHIPMENT_STATUS.RETURNED,
  ]),
  [SHIPMENT_STATUS.DELIVERED]: Object.freeze([]),
  // A returned parcel is re-processed to ship again.
  [SHIPMENT_STATUS.RETURNED]: Object.freeze([SHIPMENT_STATUS.PROCESSING]),
  [SHIPMENT_STATUS.CANCELLED]: Object.freeze([]),
  // Written only by the import and the R-74 close-out migration; no transition enters or leaves it.
  [SHIPMENT_STATUS.IMPORTED_UNKNOWN]: Object.freeze([]),
});

/**
 * The statuses a caller may REQUEST — every status some transition reaches, in lifecycle order.
 * Derived, so a new transition widens it and nothing else can.
 *
 * @type {ReadonlyArray<string>}
 */
const SHIPMENT_STATUS_ASSIGNABLE_VALUES = Object.freeze(
  SHIPMENT_STATUS_VALUES.filter((status) =>
    Object.values(SHIPMENT_STATUS_TRANSITIONS).some((targets) => targets.includes(status)),
  ),
);

/** The empty answer, shared so a caller can never mutate a status's targets through it. */
const NO_TARGETS = Object.freeze([]);

/**
 * The statuses a shipment at `status` may move to next — what a screen offers.
 *
 * Reads OWN keys only, so an unknown value (or an inherited `Object.prototype` name such as
 * `constructor`) answers `[]` rather than a function.
 *
 * @param {string} status - The shipment's current status.
 * @returns {ReadonlyArray<string>} The legal targets, in the machine's order; `[]` when terminal or
 *   unknown.
 */
function shipmentStatusTargets(status) {
  return typeof status === 'string' &&
    Object.prototype.hasOwnProperty.call(SHIPMENT_STATUS_TRANSITIONS, status)
    ? SHIPMENT_STATUS_TRANSITIONS[status]
    : NO_TARGETS;
}

/**
 * Each status's badge TONE — the map `<StatusBadgeCell statusTones>` takes (Q-MI-25: the tone rides
 * `data-tone` on the one tone pill). Attention states are `warning`, states in motion `info`, the
 * happy end `success`, the abandoned end `danger`. `imported_unknown` (a PostalMate import whose final
 * state was never recorded) is `neutral` — it was the muted, non-tone `badge-muted`, and the closed
 * tone set's no-verdict tone is neutral.
 *
 * @type {Readonly<Object<string, string>>}
 */
const SHIPMENT_STATUS_BADGE_TONES = Object.freeze({
  [SHIPMENT_STATUS.PENDING]: BADGE_TONE.WARNING,
  [SHIPMENT_STATUS.PROCESSING]: BADGE_TONE.INFO,
  [SHIPMENT_STATUS.AWAITING_PICKUP]: BADGE_TONE.INFO,
  [SHIPMENT_STATUS.SHIPPED]: BADGE_TONE.INFO,
  [SHIPMENT_STATUS.IN_TRANSIT]: BADGE_TONE.INFO,
  [SHIPMENT_STATUS.DELIVERED]: BADGE_TONE.SUCCESS,
  [SHIPMENT_STATUS.RETURNED]: BADGE_TONE.WARNING,
  [SHIPMENT_STATUS.CANCELLED]: BADGE_TONE.DANGER,
  [SHIPMENT_STATUS.IMPORTED_UNKNOWN]: BADGE_TONE.NEUTRAL,
});

/**
 * What a CARRIER's tracker says about a parcel — EasyPost's tracker `status` vocabulary, declared ONCE for
 * purchase-api (which asks the carrier for the tenant site's package-tracking lookup, DTOWNRETAIL-SITE-001 V1-b)
 * and the storefront block that words each scan. A scan is a carrier's own event, finer than the shipment
 * lifecycle above: "out for delivery" and "available for pickup" are both a shipment IN TRANSIT.
 *
 * @type {Readonly<Object<string, string>>}
 */
const CARRIER_SCAN_STATUS = Object.freeze({
  UNKNOWN: 'unknown',
  PRE_TRANSIT: 'pre_transit',
  IN_TRANSIT: 'in_transit',
  OUT_FOR_DELIVERY: 'out_for_delivery',
  AVAILABLE_FOR_PICKUP: 'available_for_pickup',
  DELIVERED: 'delivered',
  RETURN_TO_SENDER: 'return_to_sender',
  FAILURE: 'failure',
  CANCELLED: 'cancelled',
  ERROR: 'error',
});

/** @type {ReadonlyArray<string>} */
const CARRIER_SCAN_STATUS_VALUES = Object.freeze(Object.values(CARRIER_SCAN_STATUS));

/**
 * The shipment STANDING a carrier scan proves, or `null` when the scan proves none (`unknown` and `error`
 * carry no verdict, so the standing the records hold stands). A failed delivery attempt leaves the parcel with
 * the carrier — IN TRANSIT — and the scan itself says what went wrong.
 *
 * @type {Readonly<Object<string, ?string>>}
 */
const SHIPMENT_STATUS_OF_CARRIER_SCAN = Object.freeze({
  [CARRIER_SCAN_STATUS.UNKNOWN]: null,
  [CARRIER_SCAN_STATUS.PRE_TRANSIT]: SHIPMENT_STATUS.AWAITING_PICKUP,
  [CARRIER_SCAN_STATUS.IN_TRANSIT]: SHIPMENT_STATUS.IN_TRANSIT,
  [CARRIER_SCAN_STATUS.OUT_FOR_DELIVERY]: SHIPMENT_STATUS.IN_TRANSIT,
  [CARRIER_SCAN_STATUS.AVAILABLE_FOR_PICKUP]: SHIPMENT_STATUS.IN_TRANSIT,
  [CARRIER_SCAN_STATUS.DELIVERED]: SHIPMENT_STATUS.DELIVERED,
  [CARRIER_SCAN_STATUS.RETURN_TO_SENDER]: SHIPMENT_STATUS.RETURNED,
  [CARRIER_SCAN_STATUS.FAILURE]: SHIPMENT_STATUS.IN_TRANSIT,
  [CARRIER_SCAN_STATUS.CANCELLED]: SHIPMENT_STATUS.CANCELLED,
  [CARRIER_SCAN_STATUS.ERROR]: null,
});

/**
 * The shipment standing a carrier scan proves — `null` for a scan with no verdict, and for a value the
 * vocabulary does not know (a carrier's new word is no verdict either, never a crash and never a guess).
 *
 * @param {*} scan - a carrier scan status
 * @returns {?string}
 */
function shipmentStatusOfCarrierScan(scan) {
  return typeof scan === 'string' &&
    Object.prototype.hasOwnProperty.call(SHIPMENT_STATUS_OF_CARRIER_SCAN, scan)
    ? SHIPMENT_STATUS_OF_CARRIER_SCAN[scan]
    : null;
}

module.exports = {
  SHIPMENT_STATUS,
  SHIPMENT_STATUS_VALUES,
  SHIPMENT_STATUS_TRANSITIONS,
  SHIPMENT_STATUS_BADGE_TONES,
  SHIPMENT_STATUS_ASSIGNABLE_VALUES,
  CARRIER_SCAN_STATUS,
  CARRIER_SCAN_STATUS_VALUES,
  SHIPMENT_STATUS_OF_CARRIER_SCAN,
  shipmentStatusOfCarrierScan,
  shipmentStatusTargets,
};
